import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { MonitoringAccountConfigurationApiModel } from './model/monitoring-account-configuration-api.model';
import { ProminenceGroupApiModel } from './model/prominence-group-api.model';
import { NeptuneApi } from '../genericApi/neptune.api';

// Tied to backend
export enum ProminenceRecalculationJobStatus {
    PENDING = 'PENDING',
    ONGOING = 'ONGOING',
    DONE    = 'DONE',
    FAILED  = 'FAILED'
}

@Injectable()
export class MonitoringAccountConfigurationApi {

    constructor(
        private neptuneApi: NeptuneApi,
    ) { }

    /**
     * Gets all of the accounts associated with the current user
     */
    getMonitoringAccountConfigurationModel(): Observable<MonitoringAccountConfigurationApiModel> {

        const slug = '/configurations/monitoring';

        return this.neptuneApi.api<MonitoringAccountConfigurationApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET,
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    getProminenceGroups(): Observable<ProminenceGroupApiModel[]> {

        const slug = '/configurations/monitoring/prominencegroups';

        return this.neptuneApi.api(slug, null, HttpVerbsEnum.GET);
    }

    updateProminenceGroups(groups: ProminenceGroupApiModel[]): Observable<void> {

        const slug = '/configurations/monitoring/prominencegroups';

        return this.neptuneApi.api(
            slug,
            groups,
            HttpVerbsEnum.PUT
        );
    }

    getProminenceCalculationStatus(): Observable<ProminenceRecalculationJobStatus> {

        const slug = '/configurations/monitoring/prominencecalculationstatus';

        return this.neptuneApi.api(slug, null, HttpVerbsEnum.GET);
    }

    // Admin
    getConfigs(accountId: number): Observable<MonitoringAccountConfigurationApiModel> {

        const slug = `/configurations/monitoring/accounts/${accountId}`;

        return this.neptuneApi.api<MonitoringAccountConfigurationApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    // Admin
    updateConfigs(accountId: number, configs: MonitoringAccountConfigurationApiModel, locale: string): Observable<void> {

        const slug = `/configurations/monitoring/accounts/${accountId}?locale=${locale}`;

        return this.neptuneApi.api<void>(
            slug,
            configs,
            HttpVerbsEnum.PUT,
        ).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && (<HttpErrorResponse> error).status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    // Admin
    createConfigs(accountId: number, configs: MonitoringAccountConfigurationApiModel): Observable<void> {

        const slug = `/configurations/monitoring/accounts/${accountId}`;

        return this.neptuneApi.api(slug, configs, HttpVerbsEnum.POST);
    }

    // Admin
    deleteConfigs(accountId: number): Observable<void> {

        const slug = `/configurations/monitoring/accounts/${accountId}`;

        return this.neptuneApi.api(slug, null, HttpVerbsEnum.DELETE);
    }
}
